import { Reseller } from '@ViewModels';
import * as React from 'react';
import { CambridgeCompactLogoIcon } from '../CambridgeCompactLogoIcon';
import { CambridgeLogoIcon } from '../CambridgeLogoIcon';
import { CompactLogoIcon, ECompactLogoIconType } from '../CompactLogoIcon';
import { LogoIcon } from '../LogoIcon';

interface ILogoProps {
	className?: string;
	height?: number;
	width?: number;
	textColor?: string;
}

interface ICompactLogoProps {
	className?: string;
	height?: number;
	loading?: boolean;
	width?: number;
	type?: ECompactLogoIconType;
}

/**
 * Configuration for reseller-specific logo components
 */
interface IResellerLogoConfig {
	LogoComponent: React.FC<ILogoProps>;
	CompactLogoComponent: React.FC<ICompactLogoProps>;
}

/**
 * Map of reseller types to their logo components
 */
const RESELLER_LOGO_MAP: Record<Reseller, IResellerLogoConfig> = {
	[Reseller.Cambridge]: {
		LogoComponent: CambridgeLogoIcon,
		CompactLogoComponent: CambridgeCompactLogoIcon,
	},
	[Reseller.RevGen]: {
		// RevGen uses default Levitate logos for now
		LogoComponent: LogoIcon,
		CompactLogoComponent: CompactLogoIcon,
	},
	[Reseller.None]: {
		LogoComponent: LogoIcon,
		CompactLogoComponent: CompactLogoIcon,
	},
};

/**
 * Returns the appropriate full logo component for the given reseller
 * @param reseller - The reseller type (defaults to None/Levitate)
 * @returns The logo component to use
 */
export const getLogoComponent = (reseller?: Reseller): React.FC<ILogoProps> => {
	if (!reseller || reseller === Reseller.None) {
		return LogoIcon;
	}

	const config = RESELLER_LOGO_MAP[reseller];
	return config?.LogoComponent ?? LogoIcon;
};

/**
 * Returns the appropriate compact logo component for the given reseller
 * @param reseller - The reseller type (defaults to None/Levitate)
 * @returns The compact logo component to use
 */
export const getCompactLogoComponent = (reseller?: Reseller): React.FC<ICompactLogoProps> => {
	if (!reseller || reseller === Reseller.None) {
		return CompactLogoIcon;
	}

	const config = RESELLER_LOGO_MAP[reseller];
	return config?.CompactLogoComponent ?? CompactLogoIcon;
};
